import {
  EnumModuloDTONecessitaFirma,
  TipoDomandaDTO,
  type ProcedimentoConTask,
  type RichiestaDTO,
} from '@framework/api-client';
import { useCurrentUser } from '@framework/auth';
import {
  backendToRawSemanticFields,
  EngineFillableContextProvider,
  useEngineValidationContext,
  type IEngineValidationContextParams,
} from '@framework/common-web';
import {
  deserializeFormSectionsValues,
  FormValidatorBuilder,
} from '@framework/engine';
import {
  FormErrorsAlert,
  FormHandler,
  TriggerValidation,
} from '@framework/form';
import React, { useCallback, useMemo } from 'react';
import { type ISubmissionNavigator } from '../../navigation/useSubmissionNavigation';
import { SubmissionLogic } from '../../submission/SubmissionLogic';
import { useDelegationContext } from '../delegation/context/DelegationContext';
import { ModuleStateMachine } from '../fsm/ModuleStateMachine';
import { getModuleTitle } from '../getModuleTitle';
import { type IFrontOfficeModule } from '../hooks/useModule';
import { useSections } from '../section/hooks/useSections';
import { ModuleFormWrapper } from './ModuleFormWrapper';
import { SectionsFillableForm } from './SectionFillableForm';
import { ModuleFormSectionsMonitor } from './sections-monitor/ModuleFormSectionsMonitor';
import { useSubmitModuleForm } from './useSubmitModuleForm';

export interface IModuleFormProps {
  submissionId: string | null | undefined;
  module: IFrontOfficeModule;
  submission: RichiestaDTO;
  inquiry?: TipoDomandaDTO;
  dossier: ProcedimentoConTask | null | undefined;
  handleNavigation: ISubmissionNavigator;

  children?: React.ReactNode;

  before?: React.ReactNode;
  after?: React.ReactNode;

  /** Funzione chiamata al salvataggio avvenuto con successo in bozza */
  onDraftSuccess?: () => Promise<void>;
  /** Funzione chiamata al salvataggio avvenuto con successo */
  onSaveSuccess?: () => Promise<void>;

  /** Funzioni che gestiscono le operazioni da svolgere prima e dopo il salvataggio del modulo */
  onBeforeSave?: () => Promise<void>;
  onAfterSave?: (isValid: boolean) => Promise<void>;

  /** Forza il readonly del form */
  readonly?: boolean;

  /** Indica che la richiesta può essere completata in automatico senza bisogno del completamento manuale dell'utente */
  canAutoComplete?: boolean;

  /** Indica che ci troviamo nel backoffice */
  isBackoffice?: boolean;

  /** Indica se c'è una richiesta in corso del modulo in lettura */
  isFetching: boolean | undefined;
}

const ON_SUBMIT_NOOP = () => {
  /** Funzione vuota di default */
};

/**
 * Componente padre che gestisce la renderizzazione di tutte le sezioni e del relativo form
 */
export function ModuleForm(props: IModuleFormProps) {
  const {
    module,
    submission,
    inquiry,
    submissionId,
    handleNavigation,
    onDraftSuccess,
    onSaveSuccess,
    readonly,
    onAfterSave,
    onBeforeSave,
    canAutoComplete,
    isBackoffice,
    dossier,
    isFetching,
  } = props;

  const user = useCurrentUser();
  const { sections, values } = useSections(module);

  const delegationContext = useDelegationContext();

  // Gestiamo la visualizzazione del form in sola lettura.
  // Se la richiesta è in pagamento o è inviata, il modulo è in sola lettura
  const isReadonly = useMemo(() => {
    return (
      readonly ??
      // Contiene già la logica di SubmissionLogic.isSubmissionReadonly
      !ModuleStateMachine.can(
        {
          submission,
          module,
          delegationData: delegationContext?.delegationData,
        },
        '.editable',
        user,
      ).allowed
    );
  }, [readonly, submission, module, delegationContext, user]);

  /**
   * Memoizziamo il context di validazione per evitare di reimpostare
   * gli `initialValues` in modo errato.
   */
  const validationContextParams: IEngineValidationContextParams =
    useMemo(() => {
      return {
        user:
          // In BO non carichiamo i dati dell'utente loggato
          isBackoffice
            ? undefined
            : {
                ...user?.utenteLoggato,
                custom: user?.utenteLoggato?.datiCustomAggiuntivi as Record<
                  string,
                  any
                >,
              },
        semanticFields: backendToRawSemanticFields(submission.campiSemantici),
        dossierSemanticFields: backendToRawSemanticFields(
          dossier?.campiSemantici,
        ),
        // Metadati della submission raggruppati in un unico oggetto per estensibilità futura
        metadata: {
          submission: {
            phaseId: submission.fase?.id,
            phaseName: submission.fase?.denominazione,
            originalRequest: submission.richiestaOriginale,
          },
        },
      } satisfies IEngineValidationContextParams;
      // eslint-disable-next-line react-hooks/react-compiler
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      // Non aggiiungiamo user come dipendenze in quanto
      // il loro refresh non deve influire sul context di validazione e
      // soprattutto sugli `initialValues`.
      // user?.utenteLoggato,
      isBackoffice,
      submission.campiSemantici,
      submission.richiestaOriginale,
      submission.fase,
      dossier?.campiSemantici,
    ]);

  const validationContext = useEngineValidationContext(validationContextParams);

  const validationSchema = useMemo(() => {
    return FormValidatorBuilder.buildSections(sections, { isReadonly });
  }, [sections, isReadonly]);

  const initialValues = useMemo(() => {
    return validationSchema.schema?.cast(
      deserializeFormSectionsValues(validationSchema.schema, values),
      {
        assert: false,
        context: validationContext,
      },
    );
  }, [validationSchema, values, validationContext]);

  const handleSubmit = useSubmitModuleForm({
    moduleId: module.moduloRichiestaId,
    submissionId: submissionId,
    validationSchema: validationSchema.schema,
    onSuccess: onSaveSuccess,
    onBeforeSave,
    onAfterSave,
  });

  const handleSubmitFromKeyboard = useCallback(
    async (values: any) => {
      // eslint-disable-next-line no-useless-catch
      try {
        await handleSubmit(values);
      } catch (e) {
        // Il messaggio di errore è già gestito dall'hook `handleSubmit`.
        // Qui non facciamo nulla, ma siamo comunque obbligati a rilanciare
        // l'errore in quanto serve a prevenire la navigazione.
        throw e;
      }
    },
    [handleSubmit],
  );
  const moduleTitle = getModuleTitle(module);

  // Recupero al funzione di check sul readonly dei widget. A differenza del flag qui sopra, che
  // è da considerarsi globale, la funzione permette di gestire i widget caso per caso.
  // e.g. mettere in readonly solo i widget che non sono integrabili durante un'integrazione.
  const isWidgetReadonly = useMemo(
    () => SubmissionLogic.getIsWidgetReadonlyFunction(submission),
    [submission],
  );

  // TODO/MERGE Rimosso perché è la vecchia logica di delega
  // if (
  //   submission.datiDelega?.permetteCompilazione === false &&
  //   submission.idDelegato === user?.utenteLoggato?.idUtente
  // ) {
  //   isReadonly = true;
  // }

  // Se è già stato validato almeno una volta, mostriamo sempre il messaggio
  // di errore
  const showValidationOnLoad =
    !isReadonly && module.statoValidazione?.valido === false;

  if (!module?.sezioni) return null;

  return (
    <EngineFillableContextProvider
      submissionModuleId={module.moduloRichiestaId}
      sections={sections}
      submission={submission}
    >
      <FormHandler
        key={module.moduloRichiestaId ?? '<undefined>'}
        id={module.moduloRichiestaId ?? '<undefined>'}
        initialValues={initialValues}
        schema={validationSchema.schema}
        validationContext={validationContext}
        mode="all"
        onSubmit={isReadonly ? ON_SUBMIT_NOOP : handleSubmitFromKeyboard}
      >
        <ModuleFormSectionsMonitor sections={sections} />
        <FormErrorsAlert />
        <ModuleFormWrapper
          currentTitle={moduleTitle}
          submissionModuleId={module.moduloRichiestaId}
          submissionId={submissionId}
          submission={submission}
          inquiry={inquiry}
          validationSchema={validationSchema.schema}
          handleNavigation={handleNavigation}
          validationState={module.statoValidazione}
          isReadonly={isReadonly}
          onDraftSuccess={onDraftSuccess}
          moduleState={module.stato}
          verifyHandwrittenSignatureAi={module.verificaFirmaAutografaAi}
          hasSignatures={
            module.necessitaFirma !== EnumModuloDTONecessitaFirma.NO
          }
          beforeModule={props.before}
          canAutoComplete={canAutoComplete}
          onBeforeSave={onBeforeSave}
          onAfterSave={onAfterSave}
          isModuleSigned={module.idFileFirmato != null}
          isBackoffice={isBackoffice}
          isDigitallySigned={module.flagDigitalSignature}
        >
          <SectionsFillableForm
            sections={sections}
            readonly={isReadonly}
            isReadonly={isWidgetReadonly}
          />
          {props.children}
          {props.after}
          {showValidationOnLoad && (
            <TriggerValidation initialValues={initialValues} />
          )}
        </ModuleFormWrapper>
      </FormHandler>
    </EngineFillableContextProvider>
  );
}
